// src/components/BiinanceManualExecuteCard.jsx
import React, { useState } from "react";
import axios from "axios";

function normalizeSymbolToSlash(s) {
  if (!s) return "";
  const t = s.trim().toUpperCase();
  if (t.includes("/")) return t;
  if (t.includes("-")) return t.replace("-", "/");
  // assume *USDT* style, e.g., BTCUSDT -> BTC/USDT
  if (t.endsWith("USDT")) return `${t.replace("USDT","")}/USDT`;
  return t;
}

export default function ManualExecuteCard({ apiBase }) {
  const [symbol, setSymbol] = useState("BTC/USDT");
  const [side, setSide] = useState("short"); // long | short
  const [entry, setEntry] = useState("");
  const [tp, setTp]       = useState("");
  const [sl, setSl]       = useState("");
  const [qtyUsd, setQty]  = useState("");    // optional override
  const [busy, setBusy]   = useState(false);
  const [resp, setResp]   = useState(null);
  const [err, setErr]     = useState("");

  const onExec = async (e) => {
    e.preventDefault();
    setErr(""); setResp(null);

    const entryN = toDecimal(entry);
    const tpN    = toDecimal(tp);
    const slN    = toDecimal(sl);
    const qtyN   = qtyUsd ? toDecimal(qtyUsd) : null;

    if (![entryN, tpN, slN].every(Number.isFinite)) {
    setErr("Please enter valid numbers for entry / tp / sl.");
    return;
    }

    const token = normalizeSymbolToSlash(symbol);
    const isLong = side === "long";

    const suggestion = {
      token,
      analysis: {
        recommendation: isLong ? "BUY" : "SELL",
        score: 999,                      // bypass minScore filters
        trendPhase: "manual",
        expectedDirection24h: "manual",
        longPosition:  isLong  ? { entry: entryN, tp: tpN, sl: slN, riskReward: 0 } : {},
        shortPosition: !isLong ? { entry: entryN, tp: tpN, sl: slN, riskReward: 0 } : {},
      }
    };

        const body = {
        suggestions: [{
            ...suggestion,
            ...(qtyN ? { qtyUsd: qtyN } : {})
        }]
        };
        
    setBusy(true);
    try {
      // Note: qtyUsd is optional — server uses its own if not provided
      const url = `${apiBase}/api/binance/bot/execute-trades`;
      const { data } = await axios.post(url, body, { timeout: 20000 });
      setResp(data);
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message);
    } finally {
      setBusy(false);
    }
  };

  // helper: tolerant number parser
    function toDecimal(str) {
    if (str == null) return NaN;
    // remove spaces, turn comma decimals into dot, keep first numeric run
    const s = String(str).trim().replace(/\s+/g, "").replace(/,/g, ".");
    const m = s.match(/[+-]?\d*(?:\.\d+)?(?:e[+-]?\d+)?/i);
    const n = Number(m ? m[0] : NaN);
    return Number.isFinite(n) ? n : NaN;
    }

    // optional: keep UI value normalized on blur
    function normalizeForDisplay(v, set) {
    if (v == null || v === "") return;
    set(String(v).replace(/,/g, "."));
    }


  return (
    <div style={{
      background: "#0c0c14", borderRadius: 12, padding: 24, marginTop: 24,
      boxShadow: "0 0 0 1px #1f2540 inset"
    }}>
      <h2 style={{marginTop:0, color:"#fff"}}>Manual Execute</h2>
      <p style={{color:"#aab"}}>Sends a single suggestion to the executor. Uses the same tolerance, rounding, leverage, and TP/SL logic as the auto-bot.</p>

      <form onSubmit={onExec} style={{display:"grid", gap:12, gridTemplateColumns:"repeat(2, 1fr)"}}>
        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Symbol</span>
          <input
            value={symbol}
            onChange={e=>setSymbol(e.target.value)}
            placeholder="BTC/USDT or BTCUSDT"
            style={inputStyle}
          />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Side</span>
          <select value={side} onChange={e=>setSide(e.target.value)} style={inputStyle}>
            <option value="long">LONG (BUY)</option>
            <option value="short">SHORT (SELL)</option>
          </select>
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Entry</span>
          <input
            type="text"
            inputMode="decimal"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            onBlur={() => normalizeForDisplay(entry, setEntry)}
            placeholder="113200"
            style={inputStyle}
            />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Take Profit</span>
         <input
            type="text"
            inputMode="decimal"
            value={tp}
            onChange={(e) => setTp(e.target.value)}
            onBlur={() => normalizeForDisplay(tp, setTp)}
            placeholder="111800"
            style={inputStyle}
            />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Stop Loss</span>
          <input
            type="text"
            inputMode="decimal"
            value={sl}
            onChange={(e) => setSl(e.target.value)}
            onBlur={() => normalizeForDisplay(sl, setSl)}
            placeholder="113900"
            style={inputStyle}
            />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>USD Size (optional)</span>
          <input
            type="text"
            inputMode="decimal"
            value={qtyUsd}
            onChange={(e) => setQty(e.target.value)}
            onBlur={() => normalizeForDisplay(qtyUsd, setQty)}
            placeholder="leave blank to use server"
            style={inputStyle}
            />
        </label>

        <div style={{gridColumn:"1 / -1", display:"flex", gap:12}}>
          <button type="submit" disabled={busy} style={btnStyle}>
            {busy ? "Submitting…" : "Execute"}
          </button>
          <button type="button" onClick={() => { setResp(null); setErr(""); }} style={ghostBtnStyle}>
            Clear Output
          </button>
        </div>
      </form>

      {err && <div style={{marginTop:12, color:"#ff7373"}}>Error: {err}</div>}
      {resp && (
        <pre style={{
          marginTop:12, background:"#0a0f1a", color:"#cfe", padding:16,
          borderRadius:8, maxHeight:300, overflow:"auto", border:"1px solid #1f2540"
        }}>
{JSON.stringify(resp, null, 2)}
        </pre>
      )}
    </div>
  );
}

const inputStyle = {
  background:"#0a0f1a", border:"1px solid #1f2540", color:"#e9edf7",
  borderRadius:10, padding:"10px 12px", outline:"none"
};
const btnStyle = {
  background:"#2ecc71", color:"#0a0f1a", border:"none",
  borderRadius:10, padding:"10px 16px", fontWeight:700, cursor:"pointer"
};
const ghostBtnStyle = {
  background:"transparent", color:"#cfe", border:"1px solid #1f2540",
  borderRadius:10, padding:"10px 16px", cursor:"pointer"
};
